import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material';

import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import clsx from "clsx";
import ListItemText from "@mui/material/ListItemText";
import {dynamicFormStyle, MenuProps} from "../../../../styles/components/DynamicComponentStyle";
import {TicketStyle} from "../../../../styles/pages/tickets/TicketStyle";


const DynamicForm = ({
                         formName,
                         data,
                         editable,
                         isLoading,
                         clienteCensito,
                         fields
                     }) => {
    const classes = dynamicFormStyle();
    const ticketClasses = TicketStyle();


    // Stato per i valori del form
    const [formValues, setFormValues] = useState({});
    const [dirty, setDirty] = useState(false);

    // Inizializzazione dei valori del form
    useEffect(() => {
        const initialValues = {};
        fields.filter(x => x.type !== 'text-h1').forEach(field => {
            initialValues[field.name] = data && data[field.name] != null ? data[field.name] : '';
        });
        setFormValues(initialValues);
        setDirty(false);
    }, [data, fields]);

    // Funzione per ottenere le righe
    const getNumRows = () => {
        const rows = fields.reduce((acc, field) => {
            const r = field.row != null ? field.row : 0;
            if (!acc.includes(r)) acc.push(r);
            return acc;
        }, []);
        return rows.sort((a, b) => a - b);
    };

    // Restituisce i campi di una determinata riga
    const getByRow = row => {
        return fields.filter(f => (f.row != null ? f.row : 0) === row);
    };

    // Cambio valore di un campo
    const handleChange = (e, fieldName) => {
        const newValue = e.target.value;
        setFormValues(prev => {
            const updated = {...prev, [fieldName]: newValue};
            setDirty(true);
            return updated;
        });
    };

    // Funzione pulisci
    const handleReset = () => {
        const resetValues = {};
        fields.forEach(field => {
            resetValues[field.name] = '';
        });
        setFormValues(resetValues);
        setDirty(false);
    };

    // Funzione salva
    const handleSave = () => {
        console.log('Salvataggio form:', formValues);
        // Qui la tua logica/API di salvataggio...
        setDirty(false);
    };

    // Validità form (esempio semplificato: tutti i campi pieni)
    const formIsValid = fields.every(field => {
        return true;
    });

    // Helpers per select in sola lettura
    const getSelectFieldValue = field => {
        const value = formValues[field.name];
        if (!field.options) return '';
        const option = field.options.find(o => o.value === value);
        return option ? option.label : '';
    };

    const getMultiSelectFieldValue = field => {
        const values = formValues[field.name] || [];
        if (!field.options) return '';
        const labels = field.options
            .filter(opt => values.includes(opt.value))
            .map(opt => opt.label);
        return labels.join(', ');
    };


    const isSelected = (value, field) => (formValues[field.name] || []).includes(value);


    // RENDER
    return (
        <React.Fragment>
            {editable ? (
                // Modalità EDIT
                (<Card className={clsx(ticketClasses.ticketDetailPanel)}
                      style={{height: "100%", display: "flex", flexDirection: "column"}}>
                    <CardHeader
                        title={<Typography variant={"h6"}><b>{formName}</b></Typography>}
                        subheader={"Dati aggiuntivi della pratica"}
                    />
                    <CardContent className={ticketClasses.ticketDetailPanelContent}>
                        {isLoading && (
                            <div className={classes.loadingContainer}>
                                <CircularProgress size={24} style={{marginBottom: 8}}/>
                                <Typography>Caricamento in corso...</Typography>
                            </div>
                        )}

                        {!isLoading && (
                            <form>
                                {getNumRows().map(row => (
                                    <div key={row} className={classes.rowContainer}>
                                        {getByRow(row).map(field => {
                                            // Filtra in base a clienteCensito
                                            if (!(field.mostraPerCensito ?? true) && clienteCensito) {
                                                return null;
                                            }

                                            switch (field.type) {
                                                case 'text-h1':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            <Typography
                                                                className={classes.largerBold}
                                                                style={{
                                                                    textAlign: 'center',
                                                                    marginBottom: 16,
                                                                    width: '100%'
                                                                }}
                                                            >
                                                                {field.label}
                                                            </Typography>
                                                        </div>
                                                    );
                                                case 'text-simple':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            <Typography style={{fontWeight: 'bold'}}>
                                                                {field.label}
                                                            </Typography>
                                                        </div>
                                                    );
                                                case 'text-html':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            {field.label && (
                                                                <Typography
                                                                    style={{fontWeight: 'bold', marginBottom: 8}}>
                                                                    {field.label}
                                                                </Typography>
                                                            )}
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: field.labelAlt || ''
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                case 'note':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            {field.label && (
                                                                <Typography style={{fontWeight: 'bold'}}>
                                                                    {field.label}
                                                                </Typography>
                                                            )}
                                                            <Typography>{field.labelAlt}</Typography>
                                                        </div>
                                                    );
                                                case 'yellow-note':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            {field.label && (
                                                                <div className={classes.glowYellow}>
                                                                    <Typography style={{fontWeight: 'bold'}}>
                                                                        {field.label}
                                                                    </Typography>
                                                                </div>
                                                            )}
                                                            <div className={classes.glowYellow}>{field.labelAlt}</div>
                                                        </div>
                                                    );
                                                case 'text':
                                                case 'money':
                                                case 'cellular':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            {/*<Typography style={{ fontWeight: 'bold' }}>*/}
                                                            {/*    {field.label}*/}
                                                            {/*</Typography>*/}
                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                label={field.labelAlt ?? field.label}
                                                                value={formValues[field.name] ?? ''}
                                                                onChange={e => handleChange(e, field.name)}
                                                            />
                                                        </div>
                                                    );
                                                case 'checkbox':
                                                    // In Angular usavi radio "Sì/No"
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            <FormControl component="fieldset">
                                                                <FormLabel component="legend">{field.label}</FormLabel>
                                                                <RadioGroup
                                                                    row
                                                                    value={formValues[field.name] || ''}
                                                                    onChange={e => handleChange(e, field.name)}
                                                                >
                                                                    <FormControlLabel
                                                                        value="si"
                                                                        control={<Radio color={"primary"}/>}
                                                                        label="Sì"
                                                                    />
                                                                    <FormControlLabel
                                                                        value="no"
                                                                        control={<Radio color={"primary"}/>}
                                                                        label="No"
                                                                    />
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </div>
                                                    );
                                                case 'select':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            <FormControl variant="outlined" fullWidth>
                                                                <InputLabel>{field.label}</InputLabel>
                                                                <Select
                                                                    value={formValues[field.name] || ''}
                                                                    onChange={e => handleChange(e, field.name)}
                                                                    label={field.label} // Associa l'etichetta alla Select
                                                                    MenuProps={MenuProps}
                                                                >
                                                                    {field.options &&
                                                                        field.options.map(option => (
                                                                            <MenuItem key={option.value}
                                                                                      value={option.value}>
                                                                                {option.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                </Select>
                                                            </FormControl>
                                                        </div>
                                                    );
                                                case 'multiselect':
                                                    return (
                                                        <div key={field.name} className={classes.fieldItem}>
                                                            <FormControl variant="outlined" fullWidth>
                                                                <InputLabel>{field.label}</InputLabel>
                                                                <Select
                                                                    multiple
                                                                    value={formValues[field.name] || []}
                                                                    onChange={(e) => handleChange(e, field.name)}
                                                                    label={field.label} // Associa l'etichetta alla Select
                                                                    renderValue={(selected) =>
                                                                        field.options
                                                                            .filter((opt) => selected.includes(opt.value))
                                                                            .map((opt) => opt.label)
                                                                            .join(', ')
                                                                    }
                                                                    MenuProps={MenuProps}
                                                                >
                                                                    {field.options &&
                                                                        field.options.map((option) => (
                                                                            <MenuItem key={option.value}
                                                                                      value={option.value}>
                                                                                <Checkbox color={"primary"}
                                                                                          checked={isSelected(option.value, field)}/>
                                                                                <ListItemText primary={option.label}/>
                                                                            </MenuItem>
                                                                        ))
                                                                    }
                                                                </Select>
                                                            </FormControl>
                                                        </div>
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })}
                                    </div>
                                ))}
                            </form>
                        )}
                    </CardContent>
                    {!isLoading && (
                        <CardActions className={ticketClasses.detailActions}>
                            <Button
                                className={clsx(ticketClasses.ticketDetailActionButton, ticketClasses.ticketDetailActionPrimary)}
                                variant="outlined"
                                startIcon={<DeleteSweepIcon/>}
                                disabled={!dirty}
                                onClick={handleReset}
                            >
                                PULISCI FORM
                            </Button>
                            <Button
                                className={clsx(ticketClasses.ticketDetailActionButton, ticketClasses.ticketDetailActionSuccess)}
                                variant="contained"
                                color="primary"
                                disabled={!formIsValid || !dirty}
                                onClick={handleSave}
                                startIcon={<DoubleArrowIcon/>}
                            >
                                {data ? 'AGGIORNA' : 'SALVA'}
                            </Button>
                        </CardActions>
                    )}
                </Card>)
            ) : (
                // Modalità LETTURA
                (<Card className={clsx(ticketClasses.ticketDetailPanel)}
                      style={{height: "100%", display: "flex", flexDirection: "column"}}>
                    <CardHeader
                        title={<Typography variant={"h6"}><b>{formName}</b></Typography>}
                        subheader={"Qui verranno visualizzati gli avvisi di carattere generale"}
                    />
                    {!isLoading && (
                        <CardContent className={ticketClasses.ticketDetailPanelContent}>
                            <Typography className={classes.largerBold} style={{marginBottom: 16}}>
                                {formName}
                            </Typography>
                            {getNumRows().map(row => (
                                <div key={row} className={classes.rowContainer}>
                                    {getByRow(row).map(field => {
                                        if (!(field.mostraPerCensito ?? true) && clienteCensito) {
                                            return null;
                                        }

                                        switch (field.type) {
                                            case 'text-h1':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        <Typography
                                                            className={classes.largerBold}
                                                            style={{
                                                                textAlign: 'center',
                                                                marginBottom: 16,
                                                                width: '100%'
                                                            }}
                                                        >
                                                            {field.label}
                                                        </Typography>
                                                    </div>
                                                );
                                            case 'text-simple':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        <Typography style={{fontWeight: 'bold'}}>
                                                            {field.label}
                                                        </Typography>
                                                    </div>
                                                );
                                            case 'text-html':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        {field.label && (
                                                            <Typography style={{fontWeight: 'bold', marginBottom: 8}}>
                                                                {field.label}
                                                            </Typography>
                                                        )}
                                                        <div
                                                            dangerouslySetInnerHTML={{__html: field.labelAlt || ''}}
                                                        />
                                                    </div>
                                                );
                                            case 'note':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        {field.label && (
                                                            <Typography style={{fontWeight: 'bold'}}>
                                                                {field.label}
                                                            </Typography>
                                                        )}
                                                        <Typography>{field.labelAlt}</Typography>
                                                    </div>
                                                );
                                            case 'yellow-note':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        {field.label && (
                                                            <div className={classes.glowYellow}>
                                                                <Typography style={{fontWeight: 'bold'}}>
                                                                    {field.label}
                                                                </Typography>
                                                            </div>
                                                        )}
                                                        <div className={classes.glowYellow}>{field.labelAlt}</div>
                                                    </div>
                                                );
                                            case 'text':
                                            case 'money':
                                            case 'cellular':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        <Typography style={{fontWeight: 'bold'}}>
                                                            {field.label}
                                                        </Typography>
                                                        <Typography>
                                                            {field.type === 'money'
                                                                ? new Intl.NumberFormat('it-IT', {
                                                                    style: 'currency',
                                                                    currency: 'EUR'
                                                                }).format(Number(formValues[field.name] || 0))
                                                                : formValues[field.name]}
                                                        </Typography>
                                                    </div>
                                                );
                                            case 'checkbox':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        <Typography style={{fontWeight: 'bold'}}>
                                                            {field.label}
                                                        </Typography>
                                                        <Typography>
                                                            {formValues[field.name] === 'si' ? 'Sì' : 'No'}
                                                        </Typography>
                                                    </div>
                                                );
                                            case 'select':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        <Typography style={{fontWeight: 'bold'}}>
                                                            {field.label}
                                                        </Typography>
                                                        <Typography>{getSelectFieldValue(field)}</Typography>
                                                    </div>
                                                );
                                            case 'multiselect':
                                                return (
                                                    <div key={field.name} className={classes.fieldItem}>
                                                        <Typography style={{fontWeight: 'bold'}}>
                                                            {field.label}
                                                        </Typography>
                                                        <Typography>{getMultiSelectFieldValue(field)}</Typography>
                                                    </div>
                                                );
                                            default:
                                                return null;
                                        }
                                    })}
                                </div>
                            ))}
                        </CardContent>
                    )}
                </Card>)
            )}
        </React.Fragment>
    );
};

// -----------------------------------------
// DICHIARAZIONE PROPTYPES (opzionale)
// -----------------------------------------
DynamicForm.propTypes = {
    formName: PropTypes.string.isRequired,
    data: PropTypes.object,
    editable: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    clienteCensito: PropTypes.bool.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            labelAlt: PropTypes.string,
            type: PropTypes.oneOf([
                'text-h1',
                'text-simple',
                'text-html',
                'note',
                'yellow-note',
                'text',
                'money',
                'cellular',
                'checkbox',
                'select',
                'multiselect'
            ]).isRequired,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    value: PropTypes.string.isRequired
                })
            ),
            mostraPerCensito: PropTypes.bool,
            row: PropTypes.number
        })
    ).isRequired
};

export default DynamicForm;
